import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CardActionArea,
} from '@mui/material';
import { getOrderHistory, Order } from '../api/orderApi';
import { useAuth } from '../hooks/useAuth';
import { Navigate } from 'react-router-dom';

export const OrdersPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAuthenticated } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getOrderHistory();
        setOrders(data);
      } catch (err) {
        setError('Failed to load orders. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchOrders();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <Navigate to="/products" replace />;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }


  return (
    <>
      <Container maxWidth="md">
        <Typography variant="h4" gutterBottom>
          Order History
        </Typography>
        {isMobile ? (
          <Box>
            {orders.map((order) => (
              <Card key={order.id} sx={{ mb: 2 }}>
                <CardActionArea onClick={() => {
                  setSelectedOrder(order);
                  setDialogOpen(true);
                }}>
                  <CardContent>
                    <Typography variant="subtitle1">
                      <strong>Order ID:</strong> {order.id}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Date:</strong> {order.created_at ? new Date(order.created_at).toLocaleDateString() : ''}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Status:</strong> {order.status ? (order.status.charAt(0).toUpperCase() + order.status.slice(1)) : ''}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total:</strong> ₹{order.final_amount != null ? order.final_amount.toFixed(2) : '0.00'}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <TableRow
                    key={order.id}
                    hover
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setSelectedOrder(order);
                      setDialogOpen(true);
                    }}
                  >
                    <TableCell>{order.id}</TableCell>
                    <TableCell>
                      {order.created_at ? new Date(order.created_at).toLocaleDateString() : ''}
                    </TableCell>
                    <TableCell>
                      <Typography
                        color={
                          order.status === 'delivered'
                            ? 'success.main'
                            : order.status === 'confirmed'
                            ? 'info.main'
                            : 'warning.main'
                        }
                      >
                        {order.status ? (order.status.charAt(0).toUpperCase() + order.status.slice(1)) : ''}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">₹{order.final_amount != null ? order.final_amount.toFixed(2) : '0.00'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      {/* Order Details Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Order Details</DialogTitle>
        <DialogContent dividers>
          {selectedOrder && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Order ID:</strong> {selectedOrder?.id}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Date:</strong> {selectedOrder?.created_at ? new Date(selectedOrder.created_at).toLocaleString() : ''}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Status:</strong> {selectedOrder?.status ? (selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)) : ''}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Total:</strong> ₹{selectedOrder?.final_amount != null ? selectedOrder.final_amount.toFixed(2) : '0.00'}
              </Typography>
              <Box mt={2}>
                <Typography variant="h6" gutterBottom>Products</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product Name</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Rate</TableCell>
                      <TableCell>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(selectedOrder?.products || []).map((prod) => (
                      <TableRow key={prod.id}>
                        <TableCell>{prod.product?.name || ''}</TableCell>
                        <TableCell>{prod.ordered_quantity}</TableCell>
                        <TableCell>₹{prod.rate != null ? prod.rate.toFixed(2) : ''}</TableCell>
                        <TableCell>₹{prod.amount != null ? prod.amount.toFixed(2) : ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
